import { useEffect, useRef, type ReactNode } from "react";
import { AppImage as Image } from "@/shared/ui/AppImage";
import { ArrowLeft, Paperclip, Send } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { ChatMessage, MessageThread } from "../../types";

type ChatPanelLabels = {
  online: string;
  offline: string;
  placeholder: string;
  send: string;
  backToList: string;
  today: string;
  yesterday: string;
  selectConversation: string;
};

type ChatPanelProps = {
  thread: MessageThread | null;
  messages: ChatMessage[];
  draft: string;
  labels: ChatPanelLabels;
  showBackButton?: boolean;
  compact?: boolean;
  headerActions?: ReactNode;
  onBack?: () => void;
  onDraftChange: (value: string) => void;
  onSend: () => void;
};

function formatDateLabel(date: string, labels: ChatPanelLabels): string {
  const today = new Date();
  const [day, month, year] = date.split("/").map(Number);
  const messageDate = new Date(year, month - 1, day);

  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();

  if (isToday) return labels.today;
  if (isYesterday) return labels.yesterday;
  return date;
}

export function ChatPanel({
  thread,
  messages,
  draft,
  labels,
  showBackButton = false,
  compact = false,
  headerActions,
  onBack,
  onDraftChange,
  onSend,
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thread?.id]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  if (!thread) {
    return (
      <div
        className={cn(
          "flex h-full flex-col items-center justify-center bg-card-muted/20 px-6 text-center",
          !compact && "min-h-[420px]",
        )}
      >
        <p className="text-sm text-muted">{labels.selectConversation}</p>
      </div>
    );
  }

  let lastDate = "";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!compact && (
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          {showBackButton && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted lg:hidden"
              aria-label={labels.backToList}
            >
              <ArrowLeft size={18} />
            </button>
          )}
          {thread.avatar ? (
            <Image
              src={thread.avatar}
              alt={thread.name}
              width={40}
              height={40}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
              {thread.avatarFallback}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{thread.name}</p>
            <p className="text-xs text-muted">
              {thread.online ? labels.online : labels.offline}
            </p>
          </div>
          {headerActions ? (
            <div className="flex shrink-0 items-center gap-1">{headerActions}</div>
          ) : null}
        </div>
      )}

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-card-muted/15 px-4 py-4">
        {messages.map((message) => {
          const showDate = message.date !== lastDate;
          lastDate = message.date;

          return (
            <div key={message.id}>
              {showDate && (
                <div className="mb-3 flex justify-center">
                  <span className="rounded-full bg-card px-3 py-1 text-[11px] font-medium text-muted">
                    {formatDateLabel(message.date, labels)}
                  </span>
                </div>
              )}
              <div
                className={cn(
                  "flex",
                  message.sender === "me" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm",
                    message.sender === "me"
                      ? "rounded-br-md bg-primary text-white"
                      : "rounded-bl-md border border-border bg-card text-foreground",
                  )}
                >
                  <p>{message.content}</p>
                  <p
                    className={cn(
                      "mt-1 text-[10px]",
                      message.sender === "me" ? "text-white/75" : "text-muted",
                    )}
                  >
                    {message.time}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border bg-card px-4 py-3">
        <div className="flex items-end gap-2">
          <button
            type="button"
            disabled
            className="inline-flex h-10 w-10 shrink-0 cursor-not-allowed items-center justify-center rounded-lg border border-border text-muted opacity-50"
            aria-label="Pièce jointe"
          >
            <Paperclip size={18} />
          </button>
          <textarea
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={labels.placeholder}
            rows={1}
            className="max-h-28 min-h-10 flex-1 resize-none rounded-xl border border-border bg-card-muted/30 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!draft.trim()}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={labels.send}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
